/**
 * LAY7 Cloud Documentation Scripts
 */

// Search Modal State
let searchDebounceTimer = null;
let selectedResultIndex = -1;

/**
 * Open the search modal
 */
function openSearchModal() {
    const modal = document.getElementById('search-modal');
    const input = document.getElementById('search-input');

    if (modal) {
        modal.classList.remove('hidden');
        document.body.style.overflow = 'hidden';

        if (input) {
            input.value = '';
            input.focus();
            resetSearchState();
        }
    }
}

/**
 * Close the search modal
 */
function closeSearchModal() {
    const modal = document.getElementById('search-modal');

    if (modal) {
        modal.classList.add('hidden');
        document.body.style.overflow = '';
        selectedResultIndex = -1;
    }
}

/**
 * Reset search state to initial
 */
function resetSearchState() {
    document.getElementById('search-empty')?.classList.remove('hidden');
    document.getElementById('search-loading')?.classList.add('hidden');
    document.getElementById('search-no-results')?.classList.add('hidden');
    document.getElementById('search-results-list')?.classList.add('hidden');
    selectedResultIndex = -1;
}

/**
 * Perform search with debouncing
 */
function performSearch(query) {
    clearTimeout(searchDebounceTimer);

    const emptyEl = document.getElementById('search-empty');
    const loadingEl = document.getElementById('search-loading');
    const noResultsEl = document.getElementById('search-no-results');
    const resultsListEl = document.getElementById('search-results-list');

    if (query.length < 2) {
        resetSearchState();
        return;
    }

    // Show loading
    emptyEl?.classList.add('hidden');
    noResultsEl?.classList.add('hidden');
    resultsListEl?.classList.add('hidden');
    loadingEl?.classList.remove('hidden');

    searchDebounceTimer = setTimeout(async () => {
        try {
            const locale = document.documentElement.lang || 'de';
            const response = await fetch(`/${locale}/search?q=${encodeURIComponent(query)}`, {
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            if (!response.ok) throw new Error('Search failed');

            const data = await response.json();

            loadingEl?.classList.add('hidden');

            if (data.results.length === 0) {
                noResultsEl?.classList.remove('hidden');
                resultsListEl?.classList.add('hidden');
            } else {
                noResultsEl?.classList.add('hidden');
                renderSearchResults(data.results);
                resultsListEl?.classList.remove('hidden');
            }
        } catch (error) {
            console.error('Search error:', error);
            loadingEl?.classList.add('hidden');
            noResultsEl?.classList.remove('hidden');
        }
    }, 200);
}

/**
 * Render search results
 */
function renderSearchResults(results) {
    const container = document.getElementById('search-results-list');
    if (!container) return;

    selectedResultIndex = -1;

    container.innerHTML = results.map((result, index) => `
        <a
            href="${result.url}"
            class="search-result-item block px-4 py-3 hover:bg-paper-warm focus:bg-paper-warm focus:outline-none no-underline"
            data-index="${index}"
        >
            <div class="font-medium text-ink">${escapeHtml(result.title)}</div>
            ${result.description ? `<div class="text-sm text-ink-60 mt-0.5">${escapeHtml(result.description)}</div>` : ''}
            <div class="text-sm text-ink-50 mt-1 line-clamp-2">${escapeHtml(result.snippet)}</div>
        </a>
    `).join('');
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Navigate through search results with keyboard
 */
function navigateResults(direction) {
    const items = document.querySelectorAll('.search-result-item');
    if (items.length === 0) return;

    // Remove current selection
    items.forEach(item => item.classList.remove('bg-paper-warm'));

    // Calculate new index
    if (direction === 'down') {
        selectedResultIndex = (selectedResultIndex + 1) % items.length;
    } else {
        selectedResultIndex = selectedResultIndex <= 0 ? items.length - 1 : selectedResultIndex - 1;
    }

    // Apply selection
    const selectedItem = items[selectedResultIndex];
    selectedItem.classList.add('bg-paper-warm');
    selectedItem.scrollIntoView({ block: 'nearest' });
}

/**
 * Select current result
 */
function selectCurrentResult() {
    const items = document.querySelectorAll('.search-result-item');
    if (selectedResultIndex >= 0 && selectedResultIndex < items.length) {
        items[selectedResultIndex].click();
    }
}

// Close dropdowns when clicking outside
document.addEventListener('click', (e) => {
    const dropdowns = document.querySelectorAll('.relative > .absolute');
    dropdowns.forEach(dropdown => {
        if (!dropdown.parentElement.contains(e.target)) {
            dropdown.classList.add('hidden');
        }
    });
});

// Keyboard shortcuts
document.addEventListener('keydown', (e) => {
    const modal = document.getElementById('search-modal');
    const isModalOpen = modal && !modal.classList.contains('hidden');

    // Open search with Cmd/Ctrl + K
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isModalOpen) {
            closeSearchModal();
        } else {
            openSearchModal();
        }
        return;
    }

    // Handle keys when modal is open
    if (isModalOpen) {
        if (e.key === 'Escape') {
            e.preventDefault();
            closeSearchModal();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            navigateResults('down');
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            navigateResults('up');
        } else if (e.key === 'Enter') {
            e.preventDefault();
            selectCurrentResult();
        }
    }
});

// Close mobile sidebar on escape
document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
        const mobileSidebar = document.getElementById('mobile-sidebar');
        if (mobileSidebar && !mobileSidebar.classList.contains('hidden')) {
            mobileSidebar.classList.add('hidden');
        }
    }
});

// Search input handler
document.addEventListener('DOMContentLoaded', () => {
    const searchInput = document.getElementById('search-input');
    if (searchInput) {
        searchInput.addEventListener('input', (e) => {
            performSearch(e.target.value.trim());
        });
    }

    // Add anchor links to headings
    const headings = document.querySelectorAll('.prose h2, .prose h3');

    headings.forEach(heading => {
        if (heading.id) {
            const anchor = document.createElement('a');
            anchor.href = `#${heading.id}`;
            anchor.className = 'anchor-link ml-2 text-ink-40 hover:text-ink-60 no-underline opacity-0 group-hover:opacity-100 transition-opacity';
            anchor.innerHTML = '#';
            anchor.setAttribute('aria-hidden', 'true');

            heading.classList.add('group', 'relative');
            heading.appendChild(anchor);
        }
    });
});

// Expose functions globally for onclick handlers
window.openSearchModal = openSearchModal;
window.closeSearchModal = closeSearchModal;
